import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import NavigateNext from "@mui/icons-material/NavigateNext";
import { Link as RouterLink, useLocation } from "react-router-dom";

/**
 * Segments that only exist to namespace a URL and would otherwise render as a
 * dead crumb the user cannot navigate to.
 */
const HIDDEN_SEGMENTS = new Set(["admin", "profile", "search", "order"]);

const SEGMENT_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  customers: "Customers",
  users: "Manage Users",
  "domain-logs": "Domain Logs",
  number: "Number Search",
  tollfree: "Toll Free Search",
  brightlink: "New Order",
  "bulk-call-forward": "Bulk Call Forwarding",
  create: "Create Set",
  "activate-deactivate": "Activate / Deactivate",
};

function labelFor(segment: string) {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  // Numeric segments are record ids; the page itself renders the real name.
  if (/^\d+$/.test(segment)) return `#${segment}`;
  return segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AppBreadcrumbs() {
  const { pathname } = useLocation();
  const segments = pathname.split("/").filter(Boolean);

  const crumbs = segments
    .map((segment, index) => ({
      segment,
      href: `/${segments.slice(0, index + 1).join("/")}`,
    }))
    .filter(({ segment }) => !HIDDEN_SEGMENTS.has(segment));

  if (crumbs.length === 0) return null;

  return (
    <Breadcrumbs
      separator={<NavigateNext fontSize="small" />}
      aria-label="Breadcrumb"
      sx={{ fontSize: "0.8125rem" }}
    >
      {crumbs.map(({ segment, href }, index) =>
        index === crumbs.length - 1 ? (
          <Typography
            key={href}
            variant="body2"
            color="text.primary"
            sx={{ fontWeight: 600 }}
          >
            {labelFor(segment)}
          </Typography>
        ) : (
          <Link
            key={href}
            component={RouterLink}
            to={href}
            variant="body2"
            color="text.secondary"
          >
            {labelFor(segment)}
          </Link>
        )
      )}
    </Breadcrumbs>
  );
}
